/**
 * The PasswordReset Form component
 */
import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import { LoginLayout } from './LoginLayout.tsx';
import { LoginForm } from './LoginForm.tsx';
import { Get2FACode } from './Get2FACode.tsx';
import { Verify2FACode } from './Verify2FACode.tsx';

const errorToTitle = (error) => {
  if (
    error === 'User Not Found' ||
    error === 'Password or Email are invalid. Please try again.'
  ) {
    return 'Incorrect Email or Password';
  }
  // Some rare cases with 2FA when the code was never sent or if the backend in unreachable.
  if (error === '' || error === '{}') {
    return 'An error occurred.';
  }
  if (
    typeof error === 'string' &&
    error.startsWith('The requested resource') &&
    error.endsWith('was not found')
  ) {
    return 'Your one-time login code has expired.';
  }
  if (
    error ===
    'The code provided was invalid. Please check the code you have entered and try again.'
  ) {
    return 'Incorrect Access Code';
  }
  return error;
};

const errorToDescription = (error) => {
  if (
    error === 'User Not Found' ||
    error === 'Password or Email are invalid. Please try again.'
  ) {
    return 'Please double-check your email and password and try to sign in again.';
  }
  if (
    typeof error === 'string' &&
    error.startsWith('The requested resource') &&
    error.endsWith('was not found')
  ) {
    return 'Your code is only valid for 10 minutes. Please login again.';
  }
  if (
    error ===
    'The code provided was invalid. Please check the code you have entered and try again.'
  ) {
    return 'Please double check the code we sent to you and try again.';
  }
  return undefined;
};

export default function Login(props) {
  const {
    login,
    loginInvalid2FA,
    loading,
    error = undefined,
    isLoggedIn,
    is2FARequired,
    possible2faMethods = null,
    isSSOLoggedIn,
    influencer = false,
    location = {},
  } = props;

  const [methodUsed, setMethodUsed] = useState(null);
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  /**
   * Submits the login with email and password
   *
   * @param {string} email
   * @param {string} password
   */
  const handleLogin = (email, password) => {
    setLoginData({ email, password });
    setMethodUsed(null);
    login(email, password);
  };

  const handleLogin2FA = (code) => {
    login(loginData.email, loginData.password, code, methodUsed);
  };

  if ((isLoggedIn && !is2FARequired) || isSSOLoggedIn) {
    return (
      <Redirect
        to={{
          pathname: '/',
          state: { from: location },
        }}
      />
    );
  }

  return (
    <LoginLayout
      isInfluencer={influencer}
      errorTitle={errorToTitle(error)}
      errorDescription={errorToDescription(error)}
    >
      {!is2FARequired && (
        <LoginForm
          isInfluencer={influencer}
          loading={loading}
          onLogin={handleLogin}
        />
      )}
      {is2FARequired && !methodUsed && (
        <Get2FACode
          email={loginData.email}
          password={loginData.password}
          isInfluencer={influencer}
          setMethodUsed={setMethodUsed}
          onError={(err) => loginInvalid2FA(new Error(err))}
          possibleMethods={possible2faMethods}
        />
      )}
      {is2FARequired && methodUsed && (
        <Verify2FACode
          loading={loading}
          sentTo={possible2faMethods?.[methodUsed]}
          verify={handleLogin2FA}
          isInfluencer={influencer}
        />
      )}
    </LoginLayout>
  );
}

Login.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  login: PropTypes.func.isRequired,
  loginInvalid2FA: PropTypes.func.isRequired,
  influencer: PropTypes.bool,
  isLoggedIn: PropTypes.bool.isRequired,
  isSSOLoggedIn: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  is2FARequired: PropTypes.bool.isRequired,
  possible2faMethods: PropTypes.shape({
    email: PropTypes.string,
    sms: PropTypes.string,
    call: PropTypes.string,
  }),
  location: PropTypes.shape({
    key: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
    state: PropTypes.shape({}),
  }),
};
